import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';

const AgreementSignedPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const recipientId = searchParams.get('recipient');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agreement, setAgreement] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [items, setItems] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const [
          agreementRes,
          recipientsRes,
          itemsRes
        ] = await Promise.all([
          supabase
            .from('agreements')
            .select('id, title, status, total_amount, currency, accepted_at, updated_at')
            .eq('id', id)
            .single(),
          supabase
            .from('agreement_recipients')
            .select('id, full_name, email, role, status, signed_at')
            .eq('agreement_id', id)
            .order('signing_order'),
          supabase
            .from('agreement_items')
            .select('id, title, item_type, quantity, unit_price, subtotal')
            .eq('agreement_id', id)
            .order('sort_order')
        ]);

        if (agreementRes.error) throw agreementRes.error;
        setAgreement(agreementRes.data || null);
        setRecipients(recipientsRes?.data || []);
        setItems(itemsRes?.data || []);
      } catch (err) {
        console.error('Signed page load error', err);
        setError(err.message || 'Unable to load agreement summary.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id]);

  const activeRecipient = useMemo(() => {
    if (!recipientId) return null;
    return recipients.find((rec) => rec.id === recipientId) || null;
  }, [recipientId, recipients]);

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    const numeric = Number(amount);
    if (Number.isNaN(numeric)) return String(amount);
    const formatted = numeric.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return agreement?.currency ? `${formatted} ${agreement.currency}` : formatted;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-6">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        <p className="mt-4 text-sm text-gray-600">Preparing your confirmation…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-6">
        <div className="max-w-md bg-white border border-red-200 text-red-700 px-6 py-4 rounded shadow-sm space-y-2">
          <h1 className="text-lg font-semibold">Something went wrong</h1>
          <p className="text-sm">{error}</p>
          <p className="text-xs text-gray-500">
            Please contact your Ravana Migration advisor for assistance.
          </p>
          <Link to="/" className="text-sm text-blue-600 hover:underline">Return home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center px-6 py-10">
      <div className="max-w-2xl w-full bg-white border border-gray-200 rounded-lg shadow-sm p-6 space-y-6">
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 text-green-600 text-3xl">
            ✓
          </div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Thank you for signing {agreement?.title || 'the agreement'}!
          </h1>
          <p className="text-sm text-gray-600">
            Your acceptance has been recorded. A confirmation email will be shared by your advisor for your records.
          </p>
          {activeRecipient && (
            <div className="text-xs text-gray-500">
              Signed as <span className="font-medium text-gray-800">{activeRecipient.full_name}</span> ({activeRecipient.email})
              {activeRecipient.signed_at && (
                <> on {new Date(activeRecipient.signed_at).toLocaleString()}</>
              )}
            </div>
          )}
        </div>

        <section className="space-y-2 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded px-4 py-3">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-wide text-gray-500">Agreement ID</span>
            <span className="font-medium text-gray-900">{agreement?.id}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-wide text-gray-500">Status</span>
            <span className="font-medium text-gray-900">{agreement?.status}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-wide text-gray-500">Value</span>
            <span className="font-medium text-gray-900">{formatCurrency(agreement?.total_amount)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-wide text-gray-500">Updated</span>
            <span className="font-medium text-gray-900">
              {agreement?.updated_at ? new Date(agreement.updated_at).toLocaleString() : '-'}
            </span>
          </div>
        </section>

        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Recipients</h2>
          <ul className="border border-gray-200 rounded divide-y text-sm">
            {recipients.map((recipient) => (
              <li key={recipient.id} className="px-4 py-3 flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900">{recipient.full_name}</div>
                  <div className="text-xs text-gray-500">{recipient.email}</div>
                </div>
                <div className="text-xs uppercase tracking-wide">
                  <span className={
                    recipient.status === 'signed'
                      ? 'text-green-600'
                      : recipient.status === 'pending'
                        ? 'text-yellow-600'
                        : 'text-gray-500'
                  }>
                    {recipient.status}
                  </span>
                  {recipient.signed_at && (
                    <div className="text-[10px] text-gray-500">
                      {new Date(recipient.signed_at).toLocaleString()}
                    </div>
                  )}
                </div>
              </li>
            ))}
            {recipients.length === 0 && (
              <li className="px-4 py-3 text-sm text-gray-500">Recipient list unavailable.</li>
            )}
          </ul>
        </section>

        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Summary</h2>
          <ul className="space-y-1 text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded px-4 py-3">
            {items.map((item) => (
              <li key={item.id} className="flex items-center justify-between">
                <span>{item.title || item.item_type} ({item.quantity} × {formatCurrency(item.unit_price)})</span>
                <span className="font-medium text-gray-900">{formatCurrency(item.subtotal)}</span>
              </li>
            ))}
            {items.length === 0 && (
              <li className="text-xs text-gray-500">No financial items attached.</li>
            )}
          </ul>
        </section>

        <section className="text-sm text-gray-600 space-y-2">
          <p>
            If you have any questions or need a countersigned copy, contact
            <a href="mailto:agreements@ravana.com" className="text-blue-600 hover:underline"> agreements@ravana.com</a>.
          </p>
          <div className="flex items-center justify-between text-xs text-gray-500">
            <Link to="/" className="text-blue-600 hover:underline">Return to Ravana Migration</Link>
            <span>Reference: {agreement?.id?.slice(0, 8)}</span>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AgreementSignedPage;
